import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Container, Grid, Card, CardContent, IconButton, Chip, Divider, Paper, TextField, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Collapse, Checkbox, Skeleton, Menu, MenuItem } from "@mui/material";
import {
  School as SchoolIcon,
  BookmarkBorder as BookmarkIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  StarBorder as StarBorderIcon,
  Work as WorkIcon,
  LocationPin,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { getCities, getJobs } from "../../services/PartTimeJobDetailsService";
import SmartPagination from "../../components/common/SmartPagination";
import CourseCard from "../../components/common/CourseCard";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import JobCard from "../../components/common/JobCard";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Popper, ClickAwayListener } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

const SearchPartTimeJob = () => {
  const resultsTopRef = useRef(null);
  const { country_id, country_name } = useSelector((state) => state.auth || {});
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [resultsRef, resultsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const [contextMenu, setContextMenu] = useState(null);

  const [cityOptions, setCityOptions] = useState([]);
  const [cityPage, setCityPage] = useState(1);
  const [hasMoreCities, setHasMoreCities] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);

  const [citySearch, setCitySearch] = useState("");

  const filterSections = [
    { id: "city", title: "Cities", icon: <LocationPin />, dynamic: true },
  ];

  const [searchTerm, setSearchTerm] = useState("");

  const [page, setPage] = useState(1);
  const limit = 10;
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [shouldResetPage, setShouldResetPage] = useState(false);
  const cityLimit = 10;

  const [selectedFilters, setSelectedFilters] = useState({
    city_ids: [],
    country_ids: [country_id],
    search: "",
  });

  const [selectedFilterLabels, setSelectedFilterLabels] = useState({
    city_ids: [],
  });
  const navigate = useNavigate();

  const [openSection, setOpenSection] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleToggle = (event, id) => {
    setAnchorEl(event.currentTarget);
    setOpenSection(openSection === id ? null : id);
  };

  const filteredCities = cityOptions.filter((option) =>
    option?.title?.toLowerCase().includes(citySearch.toLowerCase())
  );

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const slideLeft = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };

  const slideRight = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 },
    },
  };


  const redirectDetailPage = (jobid) => {
    localStorage.setItem("jobFilters", JSON.stringify(selectedFilters));
    localStorage.setItem("jobFilterLabels", JSON.stringify(selectedFilterLabels));
    localStorage.setItem("jobseSearch", searchTerm);
    localStorage.setItem("jobPage", page);
    localStorage.setItem("selectedjobId", jobid);

    navigate(`/details/${jobid}`)
  }

  const handleSearch = () => {
    setShouldResetPage(true);
    setSelectedFilters((prev) => ({
      ...prev,
      search: searchTerm, // store keyword in same filter object
    }));

    // fetchJobs(page, {
    //   ...selectedFilters,
    //   search: searchTerm,
    // });
  };

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setShouldResetPage(true);

      setSelectedFilters((prev) => ({
        ...prev,
        search: searchTerm,
      }));
    }, 500); // wait 0.5s after user stops typing

    return () => clearTimeout(delay);
  }, [searchTerm]);

  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    const savedFilters = localStorage.getItem("jobFilters");
    const savedLabels = localStorage.getItem("jobFilterLabels");
    const savedSearch = localStorage.getItem("jobseSearch");
    const savedPage = localStorage.getItem("jobPage"); // ✅ ADD

    if (savedFilters) {
      setSelectedFilters(JSON.parse(savedFilters));
    }

    if (savedLabels) {
      setSelectedFilterLabels(JSON.parse(savedLabels));
    }

    if (savedSearch) {
      setSearchTerm(savedSearch);
    }

    if (savedPage) {
      setPage(Number(savedPage)); // ✅ RESTORE PAGE
    }
    setIsRestored(true); // ✅ important
  }, []);

  // Use TanStack Query for main job search results
  const { data: searchResults, isFetching: searchLoading } = useQuery({
    queryKey: ["jobs", page, selectedFilters, isRestored],
    queryFn: async () => {
      if (!isRestored) return null;

      const payload = {
        ...selectedFilters,
        page: page,
        limit,
      };

      const res = await getJobs(payload);
      return res.data;
    },
    enabled: isRestored,
    staleTime: 0, // IMPORTANT: Do not cache search results
    gcTime: 0,
  });

  // Sync search results with local state
  useEffect(() => {
    if (searchResults && searchResults.success) {
      setJobs(searchResults.data);
      setTotalJobs(searchResults.pagination.totalItems);
    }
  }, [searchResults]);

  // Sync loading state
  useEffect(() => {
    if (page === 1) setInitialLoading(searchLoading);
    else setLoadingMore(searchLoading);
  }, [searchLoading, page]);

  useEffect(() => {
    if (!isRestored) return; // ⛔ stop early

    if (shouldResetPage) {
      setPage(page);              // ✅ FIRST reset page
      setShouldResetPage(false);
      return;                  // ⛔ STOP API CALL THIS TIME
    }
  }, [page, selectedFilters, isRestored, shouldResetPage]);

  useEffect(() => {
    const selectedId = localStorage.getItem("selectedjobId");

    if (selectedId && jobs.length > 0) {
      setTimeout(() => {
        const element = document.getElementById(`job-${selectedId}`);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center", // 👈 center it nicely
          });
        }

        // ✅ clear after use (important)
        localStorage.removeItem("selectedjobId");
      }, 300); // wait for render
    }
  }, [jobs]);

  useEffect(() => {
    if (openSection === "city" && cityOptions.length === 0) {
      fetchCities(1);
    }
  }, [openSection]);

  const fetchJobs = async (pageNum, filters) => {
    try {
      if (pageNum === 1) setInitialLoading(true);
      else setLoadingMore(true);

      const payload = {
        ...filters,
        page: pageNum,
        limit,
      };

      const res = await getJobs(payload);

      if (res.data.success) {
        setJobs(res.data.data);
        setTotalJobs(res.data.pagination.totalItems);
      }
    } catch (error) {
      console.error("Fetch Jobs Error:", error);
    } finally {
      setInitialLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchCities = async (pageNum = 1) => {
    if (loadingCities || !hasMoreCities) return;
    setLoadingCities(true);

    try {
      const res = await getCities(pageNum, cityLimit, country_id);
      if (res.data.success == true) {
        const result = res.data;

        setCityOptions((prev) => [
          ...prev,
          ...result.data.map((item) => ({
            id: item.value,
            title: item.label,
          }))
        ]);

        setHasMoreCities(pageNum < result.pagination.totalPages);
        setCityPage(pageNum);
      } else {
        setCityOptions([]);
        setHasMoreCities(false);
        setCityPage(1);
      }
    } catch (err) {
      console.error("City fetch error:", err);
    } finally {
      setLoadingCities(false);
    }
  };



  const handleFilterToggle = (key, id, title) => {
    setShouldResetPage(true);
    setPage(1);
    setSearchTerm("");
    setSelectedFilters((prev) => {
      const exists = prev[key].includes(id);
      return {
        ...prev,
        [key]: exists ? prev[key].filter((x) => x !== id) : [...prev[key], id],
      };
    });

    setSelectedFilterLabels((prev) => {
      const exists = prev[key].find((x) => x.id === id);
      return {
        ...prev,
        [key]: exists
          ? prev[key].filter((x) => x.id !== id)
          : [...prev[key], { id, title }],
      };
    });
  };

  const hasFilters = Object.values(selectedFilterLabels).some(
    (filters) => filters.length > 0
  );

  const handleClickAway = (event) => {
    if (!openSection) return; // if already closed do nothing

    // ignore click on anchor button
    if (anchorEl && anchorEl.contains(event.target)) {
      return;
    }

    setOpenSection(null);
    setAnchorEl(null);
  };
  const handleClearFilters = () => {
    setSelectedFilters({
      city_ids: [],
      country_ids: [country_id],
      search: "",
    });

    setSelectedFilterLabels({
      city_ids: [],
    });

    setSearchTerm("");

    // ✅ clear storage also
    localStorage.removeItem("jobFilters");
    localStorage.removeItem("jobFilterLabels");
    localStorage.removeItem("jobseSearch");
    localStorage.removeItem("jobPage");
  }

  return (
    <>
      <Container maxWidth="xl" sx={{
        py: { xs: 2, sm: 2, md: 3 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        <Box sx={{ mb: { xs: 3, md: 3 }, width: "100%" }}>
          {/* Page Title */}
          <Box
            sx={{
              display: "inline-block",
              position: "relative",
              textAlign: "center",
              width: "100%",

              "&::after": {
                content: '""',
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: -5,
                width: "200px",
                height: "2px",
                backgroundColor: "#fd0000",
                opacity: "0.5"
              },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "24px", sm: "28px", md: "30px" },
                lineHeight: '1.3 !important',
                fontWeight: 700,
                color: "#000",
                mb: 1,
                textAlign: { xs: "left", md: "center" },
              }}
            >
              Part Time Jobs Search
            </Typography>
          </Box>
          {/* Page Brief Description */}
          <Typography
            sx={{
              fontSize: { xs: "14px", md: "16px" },
              color: "#000",
              textAlign: { xs: "left", md: "center" },
              maxWidth: "700px",
              margin: "0 auto",
              mt: 2,
            }}
          >
            Find the right part time jobs based on your near by city.
          </Typography>

          {/* Search Bar */}
          <Box
            sx={{
              maxWidth: "600px",
              margin: "0 auto",
              mb: 2,
              mt: 2,
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by job name, contact, or email address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "10px",

                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  paddingRight: "50px", // ✅ IMPORTANT (space for icon)

                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#002045",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#002045",
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSearch}
              sx={{
                position: "absolute",
                right: 8,
                backgroundColor: "#002045",
                color: "#fff",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#002045" },

              }}
            >
              <SearchIcon />
            </IconButton>
          </Box>

          {/* Results & Applied Filters Overview */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: hasFilters ? "space-between" : "center",
              alignItems: { xs: "flex-start", md: "center" },
              mb: 3,
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px", md: "20px" },
                fontWeight: 600,
                color: "#000",
                whiteSpace: "nowrap !important",
              }}
            >
              {hasFilters
                ? `${totalJobs} Results For Your Criteria`
                : `${totalJobs} Total Jobs`}
            </Typography>

            {hasFilters && (<Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#555",
                  whiteSpace: "nowrap",
                }}
              >
                Your Criteria:
              </Typography>

              {/* CHIPS CONTAINER */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  maxWidth: "100%",
                }}
              >
                {Object.entries(selectedFilterLabels).map(([key, filters]) =>
                  filters.map((item) => (
                    <Chip
                      key={item.id}
                      label={item.title}
                      size="small"
                      sx={{
                        backgroundColor: "#fd0000",
                        color: "#fff",
                        fontWeight: 500,
                        borderRadius: "20px",
                        fontSize: "12px",
                        height: "28px",
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
            )}
          </Box>
        </Box>

        {/* ---------- SIDEBAR ---------- */}
        <Box
          sx={{
            width: "100%",
            // flex: { xs: "1 1 100%", md: "0 0 25%" },
            // maxWidth: { md: "320px" },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              pb: 2,
              borderRadius: "16px",
              transition: "height 0.3s ease",
            }}
          >
            {/* <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "20px", sm: "22px", md: "24px" },
              fontWeight: 700,
              color: "#ffffff",
              mb: 3,
            }}
          >
            Part Time Jobs
          </Typography> */}

            <List sx={{ p: 0, display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto' }}>
              {filterSections.map((section) => (
                <Box
                  key={section.id}
                  sx={{
                    position: "relative",
                    flex: "0 0 auto",
                    minWidth: 170
                  }}>
                  <Box
                    onClick={(e) => handleToggle(e, section.id)}
                    sx={{
                      display: 'flex',
                      justifyItems: 'center',
                      alignItems: 'center',
                      px: 1,
                      py: 1,
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: 'white',
                      border: '1px solid gray',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: "#002045" }}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "black", whiteSpace: 'nowrap' }}>
                          {section.title}
                        </Typography>
                      }
                    />
                    {openSection === section.id ? (
                      <ExpandLess
                        sx={{ fontSize: "20px", color: "#002045", pointerEvents: "none", }}
                      />
                    ) : (
                      <ExpandMoreIcon
                        sx={{ fontSize: "20px", color: "#002045", pointerEvents: "none", }}
                      />
                    )}
                  </Box>

                  {/* ---------- City DYNAMIC LIST ---------- */}
                  {/* {section.id === "city" && (
                  <Collapse
                    in={openSection === section.id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List
                      component="div"
                      disablePadding
                      onScroll={(e) => {
                        const bottom =
                          Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                          e.target.scrollHeight - 1;

                        if (bottom && hasMoreCities) {
                          fetchCities(cityPage + 1);
                        }
                      }}
                      sx={{
                        maxHeight: 200,
                        overflowY: "auto",
                      }}
                    >
                      {!loadingCities && cityOptions.length === 0 && (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 2,
                            color: "#ffffff",
                            fontSize: "14px",
                          }}
                        >
                          No cities found
                        </Box>
                      )}
                      {cityOptions && cityOptions.length > 0 && cityOptions.map((option) => (
                        <ListItem key={option.id}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.city_ids.includes(option.id)}
                            onChange={() => handleFilterToggle("city_ids", option.id, option.title)}
                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "#ffffff",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                      {loadingCities && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 1,
                          }}
                        >
                          <CircularProgress
                            size={20}
                            sx={{ color: "#fd0000" }}
                          />
                        </Box>
                      )}
                    </List>
                  </Collapse>
                )} */}
                </Box>
              ))}
              {filterSections.length > 0 && (
                <Typography
                  sx={{
                    color: "#002045",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "none",
                    whiteSpace: 'nowrap'
                  }}
                  onClick={handleClearFilters}
                >
                  Clear Filter
                </Typography>
              )}
            </List>

            {/* jobtype */}
            <ClickAwayListener onClickAway={handleClickAway}>
              <Popper
                open={openSection === "city"}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300 }}
              >
                <Paper
                  sx={{
                    minWidth: 220,
                    maxHeight: 400,
                    overflowY: "auto",
                    border: "1px solid gray",
                    borderRadius: "6px",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.1)"
                  }}
                >
                  <List
                    component="div"
                    disablePadding
                    // onScroll={(e) => {
                    //   const bottom =
                    //     Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
                    //     e.target.scrollHeight - 1;

                    //   if (bottom && hasMoreCities) {
                    //     fetchCities(cityPage + 1);
                    //   }
                    // }}
                    sx={{
                      maxHeight: 200,
                      overflowY: "auto",
                      pt: 0,
                      pb: 1,
                      px: 1
                    }}
                  >
                    {!loadingCities && cityOptions.length === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "#002045",
                          fontSize: "14px",
                        }}
                      >
                        No cities found
                      </Box>
                    )}
                    <Box
                      sx={{
                        p: 1,
                        position: "sticky",
                        top: 0,
                        zIndex: 20,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search city..."
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                        sx={{
                          backgroundColor: "#fff",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                          },
                        }}
                      />
                    </Box>
                    {cityOptions && filteredCities.length > 0 && [...filteredCities]
                      .sort((a, b) =>
                        (a?.title || "").localeCompare(b?.title || "")
                      ).map((option) => (
                        <ListItem key={option.id} onClick={() => handleFilterToggle("city_ids", option.id, option.title)}
                          sx={{ display: 'flex', justifyContent: 'flex-start', cursor: "pointer", p: 0 }}>
                          <Checkbox
                            size="small"
                            sx={{ color: "#fd0000", "&.Mui-checked": { color: "#fd0000" }, }}
                            checked={selectedFilters.city_ids.includes(option.id)}

                          />
                          <ListItemText primary={option.title} sx={{
                            "& .MuiListItemText-primary": {
                              color: "#002045",
                              fontWeight: 500, // optional
                            },
                          }} />
                        </ListItem>
                      ))}

                    {loadingCities && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress
                          size={20}
                          sx={{ color: "#fd0000" }}
                        />
                      </Box>
                    )}
                  </List>
                </Paper>
              </Popper>
            </ClickAwayListener>
          </Paper>
        </Box>

        <Divider sx={{ backgroundColor: "#002045", mb: 2, height: '1px' }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 4 },
            alignItems: "flex-start",
          }}
        >
          {/* ---------- RESULTS SECTION ---------- */}
          <Box ref={(el) => {
            resultsTopRef.current = el;
          }} sx={{ flex: "1 1 70%", width: "100%" }}>
            {!initialLoading && jobs.length === 0 && (
              <Typography sx={{ textAlign: "center", py: 4, color: "#777" }}>
                No Part time jobs found
              </Typography>
            )}
            {initialLoading ? (
              Array.from({ length: limit }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={150} sx={{ mb: 3, borderRadius: 2 }} />
              ))
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: { xs: 3, md: 4 },
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 3, md: 4 },
                    position: "relative",
                    opacity: loadingMore ? 0.5 : 1, // ✅ dim data while loading next
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {jobs.map((job) => (
                    <Box
                      id={`job-${job.id}`}
                      onContextMenu={(e) => {
                        e.preventDefault(); // ❌ stop default browser menu

                        setContextMenu({
                          mouseX: e.clientX,
                          mouseY: e.clientY,
                          jobId: job.id,
                        });
                      }}
                    >
                      <JobCard
                        key={job.id}
                        job={job}
                        loading={loadingMore}
                        onClick={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            window.open(`/details/${job.id}`, "_blank");
                          } else {
                            redirectDetailPage(job.id);
                          }
                        }}
                      // onClick={() => redirectDetailPage(job.id)}
                      />
                    </Box>
                  ))}
                </Box>

                {/* ---------- Full overlay loader for smooth transition ---------- */}
                {loadingMore && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(255,255,255,0.6)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "16px",
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: "#FF9500" }} />
                  </Box>
                )}

                {/* ---------- PAGINATION ---------- */}
                <SmartPagination
                  count={Math.ceil(totalJobs / limit)}
                  page={page}
                  // onChange={(e, value) => setPage(value)}
                  onChange={(e, value) => {
                    setPage(value);

                    setTimeout(() => {
                      resultsTopRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 100);
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Container>
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            redirectDetailPage(contextMenu.jobId);
            setContextMenu(null);
          }}
        >
          Open
        </MenuItem>
        <MenuItem
          onClick={() => {
            window.open(`/details/${contextMenu.jobId}`, "_blank");
            setContextMenu(null);
          }}
        >
          Open in new tab
        </MenuItem>
      </Menu>
    </>
  );
};

export default SearchPartTimeJob;

